import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Shield } from 'lucide-react';
import { getConsent, setConsent, hasConsented } from '../utils/cookieConsent';

export default function CookieBanner() {
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [statistics, setStatistics] = useState(false);

  // App-like routes: kein Cookie-Banner (PWA-Feeling, geschützte/eingeloggte Bereiche)
  const path = location.pathname;
  const isAppRoute = path.startsWith('/crm') || path === '/login'
    || path.startsWith('/portal') || path.startsWith('/mitarbeiter')
    || path.startsWith('/partner')
    || path.startsWith('/pv-angebot/sign/') || path.startsWith('/dach-angebot/sign/') || path.startsWith('/ext-sign/');

  useEffect(() => {
    if (isAppRoute) { setVisible(false); return; }
    if (!hasConsented()) {
      const t = setTimeout(() => setVisible(true), 400);
      return () => clearTimeout(t);
    }
  }, [isAppRoute]);

  const accept = () => { setConsent({ essential: true, marketing: true, statistics: true }); setVisible(false); };
  const reject = () => { setConsent({ essential: true, marketing: false, statistics: false }); setVisible(false); };
  const saveSettings = () => { setConsent({ essential: true, marketing, statistics }); setVisible(false); setShowSettings(false); };

  if (!visible) return null;

  return (
    <div data-testid="cookie-banner" className="fixed inset-x-0 bottom-0 z-[99999] flex justify-center p-3 sm:p-6 pointer-events-none">
      <div className="pointer-events-auto w-full max-w-[600px] bg-white/96 backdrop-blur-xl border border-stone-200/70 shadow-[0_18px_50px_-18px_rgba(15,61,46,0.22)] rounded-[22px] sm:rounded-[26px] overflow-hidden transition-all duration-500 animate-[slideUp_0.6s_ease-out]">

        {!showSettings ? (
          /* ── MAIN VIEW ── */
          <div className="p-5 sm:p-6">
            <div className="flex items-start gap-3 mb-5">
              <div className="flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center"
                style={{ background: 'rgba(63,102,72,0.08)', border: '1px solid rgba(63,102,72,0.14)' }}>
                <Shield className="w-[15px] h-[15px]" style={{ color: '#3F6648' }} />
              </div>
              <div className="min-w-0">
                <p className="text-[15px] font-semibold tracking-tight text-stone-900 leading-snug">
                  Ihre Privatsphäre
                </p>
                <p className="text-[12.5px] text-stone-500 leading-relaxed mt-1.5">
                  Wir nutzen Cookies, um Ihnen die bestmögliche Erfahrung zu bieten.
                  Einige sind notwendig, andere helfen uns, unsere Website zu verbessern.{' '}
                  <Link to="/datenschutz" className="font-medium underline underline-offset-2 transition-colors"
                    style={{ color: '#3F6648' }}
                    onMouseEnter={(e) => e.currentTarget.style.color = '#4F7A52'}
                    onMouseLeave={(e) => e.currentTarget.style.color = '#3F6648'}>
                    Mehr erfahren
                  </Link>
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-stretch gap-2">
              <button data-testid="cookie-accept-btn" onClick={accept}
                className="gle-cta flex-1 text-[13px] font-semibold rounded-xl px-5 py-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                style={{ '--tw-ring-color': '#3F6648' }}>
                Alle akzeptieren
              </button>
              <button data-testid="cookie-reject-btn" onClick={reject}
                className="flex-1 text-[13px] font-semibold rounded-xl px-5 py-3 transition-all duration-200 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                style={{ background: '#FFFFFF', color: '#1F2A26', border: '1px solid #D9D4C7' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#F5F2EA'; e.currentTarget.style.borderColor = '#1F2A26'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = '#FFFFFF'; e.currentTarget.style.borderColor = '#D9D4C7'; }}>
                Nur notwendige
              </button>
              <button data-testid="cookie-settings-btn" onClick={() => setShowSettings(true)}
                className="text-[12.5px] font-medium underline underline-offset-4 transition-colors px-3 py-2.5 focus-visible:outline-none rounded-lg"
                style={{ color: '#6B7670' }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#3F6648'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#6B7670'}>
                Einstellungen
              </button>
            </div>
          </div>
        ) : (
          /* ── SETTINGS VIEW ── */
          <div className="p-5 sm:p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-[15px] font-semibold tracking-tight text-stone-900">Cookie-Einstellungen</h3>
              <button onClick={() => setShowSettings(false)}
                className="text-[12.5px] font-medium transition-colors focus-visible:outline-none rounded-lg px-2 py-1"
                style={{ color: '#9AA39E' }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#3F6648'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#9AA39E'}>
                Zurück
              </button>
            </div>

            <div className="space-y-2 mb-5">
              {/* Essential - always on */}
              <div className="flex items-center justify-between px-4 py-3 rounded-xl"
                style={{ background: '#FAF7F0', border: '1px solid #ECE7DA' }}>
                <div className="pr-4">
                  <p className="text-[13px] font-semibold text-stone-900">Notwendig</p>
                  <p className="text-[11px] text-stone-400 mt-0.5 leading-relaxed">Grundfunktion der Website</p>
                </div>
                <div className="w-11 h-[26px] rounded-full flex items-center justify-end px-[3px] opacity-70 cursor-not-allowed"
                  style={{ background: '#3F6648' }}>
                  <div className="w-5 h-5 rounded-full bg-white shadow-sm" />
                </div>
              </div>

              {/* Marketing */}
              <label className="flex items-center justify-between px-4 py-3 rounded-xl cursor-pointer transition-colors group"
                style={{ background: '#FAF7F0', border: '1px solid #ECE7DA' }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#F4EFE2'}
                onMouseLeave={(e) => e.currentTarget.style.background = '#FAF7F0'}>
                <div className="pr-4">
                  <p className="text-[13px] font-semibold text-stone-900">Marketing</p>
                  <p className="text-[11px] text-stone-400 mt-0.5 leading-relaxed">Google Ads, Meta Pixel, GTM</p>
                </div>
                <input type="checkbox" className="sr-only peer" checked={marketing} onChange={e => setMarketing(e.target.checked)} />
                <div className="relative w-11 h-[26px] rounded-full transition-colors duration-300 flex-shrink-0"
                  style={{ background: marketing ? '#3F6648' : '#D6D1C2' }}>
                  <div className={`absolute top-[3px] w-5 h-5 rounded-full bg-white shadow-sm transition-transform duration-300 ${marketing ? 'translate-x-[22px]' : 'translate-x-[3px]'}`} />
                </div>
              </label>

              {/* Statistics */}
              <label className="flex items-center justify-between px-4 py-3 rounded-xl cursor-pointer transition-colors group"
                style={{ background: '#FAF7F0', border: '1px solid #ECE7DA' }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#F4EFE2'}
                onMouseLeave={(e) => e.currentTarget.style.background = '#FAF7F0'}>
                <div className="pr-4">
                  <p className="text-[13px] font-semibold text-stone-900">Statistik</p>
                  <p className="text-[11px] text-stone-400 mt-0.5 leading-relaxed">PostHog Analyse</p>
                </div>
                <input type="checkbox" className="sr-only peer" checked={statistics} onChange={e => setStatistics(e.target.checked)} />
                <div className="relative w-11 h-[26px] rounded-full transition-colors duration-300 flex-shrink-0"
                  style={{ background: statistics ? '#3F6648' : '#D6D1C2' }}>
                  <div className={`absolute top-[3px] w-5 h-5 rounded-full bg-white shadow-sm transition-transform duration-300 ${statistics ? 'translate-x-[22px]' : 'translate-x-[3px]'}`} />
                </div>
              </label>
            </div>

            <div className="flex gap-2">
              <button data-testid="cookie-save-settings-btn" onClick={saveSettings}
                className="gle-cta flex-1 text-[13px] font-semibold rounded-xl px-5 py-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                style={{ '--tw-ring-color': '#3F6648' }}>
                Auswahl speichern
              </button>
              <button onClick={accept}
                className="flex-1 text-[13px] font-semibold rounded-xl px-5 py-3 transition-all duration-200 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                style={{ background: '#FFFFFF', color: '#1F2A26', border: '1px solid #D9D4C7' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#F5F2EA'; e.currentTarget.style.borderColor = '#1F2A26'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = '#FFFFFF'; e.currentTarget.style.borderColor = '#D9D4C7'; }}>
                Alle akzeptieren
              </button>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(24px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

export function CookieSettingsButton() {
  const handleOpen = () => {
    localStorage.removeItem('gle_cookie_consent');
    window.location.reload();
  };
  return (
    <button data-testid="cookie-settings-reopen" onClick={handleOpen}
      className="text-sm underline underline-offset-2 hover:text-white transition-colors cursor-pointer bg-transparent border-none p-0">
      Cookie-Einstellungen
    </button>
  );
}
